import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Settings, LogOut, ExternalLink, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth";
import { api, money } from "@/lib/api";
import { SCHEMAS, MENU, EDITOR_HIDDEN } from "./schemas";
import ResourceManager from "./ResourceManager";
import SettingsPage from "./Settings";

function Nav({ user, onNav }) {
  const cls = ({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`;
  return (
    <nav className="space-y-6" onClick={onNav}>
      <NavLink to="/admin" end className={cls} data-testid="admin-nav-dashboard"><LayoutDashboard className="h-4 w-4" />Dashboard</NavLink>
      {MENU.map(([group, items]) => {
        const list = items.filter((r) => user.role === "admin" || !EDITOR_HIDDEN.includes(r));
        return list.length ? (
          <div key={group}>
            <p className="px-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{group}</p>
            <div className="mt-2 space-y-0.5">{list.map((r) => <NavLink key={r} to={`/admin/${r}`} className={cls} data-testid={`admin-nav-${r}`}>{SCHEMAS[r].title}</NavLink>)}</div>
          </div>
        ) : null;
      })}
      {user.role === "admin" && <NavLink to="/admin/configuracion" className={cls} data-testid="admin-nav-settings"><Settings className="h-4 w-4" />Configuración y seguridad</NavLink>}
    </nav>
  );
}

const Stat = ({ label, value, id }) => (
  <div className="card-cc !p-5" data-testid={id}><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className="mt-2 font-display text-2xl font-bold">{value}</p></div>
);
const fmtMulti = (o) => (Object.keys(o || {}).length ? Object.entries(o).map(([c, v]) => money(v, c)).join(" · ") : money(0));

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-slate-500">Cargando…</p>;
  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Datos reales registrados por la plataforma (sin datos ficticios).</p>
      {d.email_pending_config > 0 && <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="ses-warning">Hay {d.email_pending_config} notificaciones por correo en cola: configure las credenciales de Amazon SES para enviarlas.</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat id="stat-visitors" label="Visitas (con consentimiento)" value={d.visitors} />
        <Stat id="stat-contacts" label={`Solicitudes comerciales (${d.contacts_new} nuevas)`} value={d.contacts} />
        <Stat id="stat-demos" label="Demostraciones solicitadas" value={d.demos} />
        <Stat id="stat-sales" label={`Ventas (${d.orders_paid} pedidos)`} value={fmtMulti(d.sales)} />
        <Stat id="stat-subs" label="Suscripciones activas" value={d.active_subscriptions} />
        <Stat id="stat-mrr" label="Ingresos recurrentes (MRR)" value={fmtMulti(d.mrr)} />
        <Stat id="stat-conversion" label={`Conversión checkout (${d.checkout_started} iniciados)`} value={`${d.conversion}%`} />
        <div className="card-cc !p-5" data-testid="stat-top-products"><p className="text-xs uppercase tracking-wider text-slate-500">Productos más consultados</p>
          <ul className="mt-2 space-y-1 text-sm">{d.top_products.length ? d.top_products.map((t) => <li key={t.name} className="flex justify-between"><span>{t.name}</span><b>{t.views}</b></li>) : <li className="text-slate-400">Sin datos</li>}</ul></div>
      </div>
    </div>
  );
}

export default function AdminApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const out = async () => { await logout(); navigate("/"); };
  return (
    <div className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[260px_1fr]" data-testid="admin-app">
      <aside className="hidden bg-navy p-5 lg:block lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto">
        <p className="mb-6 font-display text-lg font-semibold text-white">Cloud Center <span className="font-mono text-xs text-signal">ADMIN</span></p>
        <Nav user={user} />
      </aside>
      <div>
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button className="lg:hidden" aria-label="Menú admin" data-testid="admin-mobile-menu"><Menu className="h-5 w-5" /></button></SheetTrigger>
            <SheetContent side="left" className="w-72 overflow-y-auto bg-navy border-0"><SheetTitle className="sr-only">Admin</SheetTitle><div className="mt-6"><Nav user={user} onNav={() => setOpen(false)} /></div></SheetContent>
          </Sheet>
          <span className="text-sm text-slate-500">{user.email} · <b className="text-slate-700">{user.role}</b>{!user.mfa_enabled && user.role === "admin" && <Link to="/admin/configuracion" className="ml-2 text-amber-600 underline" data-testid="mfa-reminder">Active MFA</Link>}</span>
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brand" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" />Sitio</Link>
            <button onClick={out} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-red-600" data-testid="admin-logout"><LogOut className="h-4 w-4" />Salir</button>
          </div>
        </header>
        <main className="p-6 lg:p-10">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="configuracion" element={<SettingsPage />} />
            <Route path=":res" element={<ResourceManager user={user} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
